import {
  APIGetRequest,
  APIPostRequest,
  APIPutRequest,
  CheckDetails,
  GetCheckDetailsResponse,
  OrderFormat,
  PaymentMethodType,
  PaymentType,
  SalesLocationType,
  CheckMetadataType,
} from '@ancon/wildcat-types'

import endpoints from '../endpoints'
import Request from '../../client/Request'
import { AdyenPaymentMethodsResponse } from '../../../features/adyen/types'
import { CheckOnlinePaymentResult } from '../../../features/check/types'
import { CheckOnlinePaymentAPIVersion } from '../../../features/check/constants'

type ChecksAPI = {
  get: {
    details: APIGetRequest<
      GetCheckDetailsResponse,
      { outletId: string; checkId: string; 'extent.outlet'?: boolean }
    >
    onlinePaymentMethods: APIGetRequest<
      {
        paymentProviderData: AdyenPaymentMethodsResponse
        useBetaOnline: boolean
      },
      {
        outletId: string
        checkId: string
        paymentProviderData: string
        methodType: PaymentMethodType
      }
    >
    status: APIGetRequest<
      {
        status: number
      },
      { outletId: string; checkId: string }
    >
    /** 204 while the payment worker is running */
    onlinePaymentStatus: APIGetRequest<
      CheckOnlinePaymentResult | '',
      { outletId: string; checkId: string; workerId: string }
    >
  }
  post: {
    create: APIPostRequest<
      { check: CheckDetails },
      {
        orderFormat: OrderFormat
        salesLocationId: string
        salesLocationType: SalesLocationType
      },
      { outletId: string }
    >
    /** 202 with the payment worker id */
    createOnlinePayment: APIPostRequest<
      { id: string },
      {
        paymentProviderData: string
        methodType: PaymentMethodType
        paymentType: PaymentType
        tipAmount?: number
      },
      { outletId: string; checkId: string }
    >
    /** 202 with the payment worker id */
    onlinePaymentDetails: APIPostRequest<
      { id: string },
      { paymentProviderData: string },
      { outletId: string; checkId: string }
    >
    cancelOnlinePayment: APIPostRequest<
      { check: CheckDetails },
      {},
      { outletId: string; checkId: string }
    >
    createSurfboardOnlinePayment: APIPostRequest<
      {
        check: CheckDetails
        payment: { id: string }
        paymentProviderData: any
      },
      {
        tipAmount?: number
      },
      {
        outletId: string
        checkId: string
      }
    >
  }
  put: {
    metadata: APIPutRequest<
      {
        check: CheckDetails
      },
      {
        type: CheckMetadataType
        reference: string
        metadata: string
      },
      {
        outletId: string
        checkId: string
      }
    >
    extractCertainItems: APIPutRequest<
      {
        originalCheck: CheckDetails
        mergeBackAt: string
        extractedCheck: CheckDetails
      },
      {
        extractCheckItems: Record<string, number>
      },
      {
        outletId: string
        checkId: string
      }
    >
    extractPartial: APIPutRequest<
      {
        originalCheck: CheckDetails
        mergeBackAt: string
        extractedCheck: CheckDetails
      },
      {
        parts: number
        extractParts: number
      },
      {
        outletId: string
        checkId: string
      }
    >
    mergeWithParent: APIPutRequest<
      {
        parentCheck: CheckDetails
      },
      {},
      {
        outletId: string
        checkId: string
      }
    >
    sendReceipt: APIPutRequest<
      {},
      { toEmail: string },
      { outletId: string; checkId: string; paymentId: string }
    >
    downloadReceipt: APIPutRequest<
      Blob,
      {},
      { outletId: string; checkId: string; paymentId: string }
    >
    discount: APIPutRequest<
      {},
      { code: string },
      { outletId: string; checkId: string }
    >
  }
}

const checks: ChecksAPI = {
  get: {
    details: new Request(endpoints.core.outlets.byId.checks.byId.details, {
      version: 1.4,
    }).get,
    onlinePaymentMethods: new Request(
      endpoints.core.outlets.byId.checks.byId.paymentMethods.online.list,
    ).get,
    status: new Request(endpoints.core.outlets.byId.checks.byId.status.list, {
      version: 1.4,
    }).get,
    onlinePaymentStatus: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.online.status.byId
        .details,
      {
        version: CheckOnlinePaymentAPIVersion,
      },
    ).get,
  },
  post: {
    create: new Request(endpoints.core.outlets.byId.checks.list, {
      version: 1.2,
    }).post,
    createOnlinePayment: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.online.list,
      {
        version: CheckOnlinePaymentAPIVersion,
      },
    ).post,
    onlinePaymentDetails: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.online.details.list,
      {
        version: CheckOnlinePaymentAPIVersion,
      },
    ).post,
    cancelOnlinePayment: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.online.cancel.list,
      {
        version: 1.4,
      },
    ).post,
    createSurfboardOnlinePayment: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.surfboard.online.order
        .list,
      {
        version: 1.4,
      },
    ).post,
  },
  put: {
    metadata: new Request(
      endpoints.core.outlets.byId.checks.byId.metadata.list,
      {
        version: 1.4,
      },
    ).put,
    extractCertainItems: new Request(
      endpoints.core.outlets.byId.checks.byId.extractCertainItems.list,
      {
        version: 1.4,
      },
    ).put,
    extractPartial: new Request(
      endpoints.core.outlets.byId.checks.byId.extractPartial.list,
      {
        version: 1.4,
      },
    ).put,
    mergeWithParent: new Request(
      endpoints.core.outlets.byId.checks.byId.mergeWithParent.list,
      {
        version: 1.4,
      },
    ).put,
    sendReceipt: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.byId.sendReceipt.list,
      {
        version: 1.4,
      },
    ).put,
    downloadReceipt: new Request(
      endpoints.core.outlets.byId.checks.byId.payments.byId.downloadReceipt
        .list,
      {
        version: 1.4,
      },
    ).put,
    discount: new Request(endpoints.core.outlets.byId.checks.byId.discount.list)
      .put,
  },
}

export default checks
